import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';
import {
  MUSIC_VIDEO_EFFECT_KINDS,
  MUSIC_VIDEO_EFFECTS,
  MUSIC_VIDEO_MAX_EFFECTS,
  type MusicVideoEffectFamily,
  type MusicVideoEffectKind,
} from 'fym-shared/src/music24/musicVideoEffects';
import { TypedMemo } from '../../common/types';
import { newDocId } from '../doc/clipIds';
import { addEffect } from '../doc/effectOps';
import { useMusicVideoEditor } from '../MusicVideoEditorContext';
import { effectFamilyMessages, effectKindMessages, effectMessages } from '../messages/effectMessages';
import { formatTime } from './timelineGeometry';
import styles from './AddEffectMenu.module.scss';

const FAMILY_ORDER: readonly MusicVideoEffectFamily[] = ['lyric', 'title', 'transition', 'shot'];

const FAMILY_CLASS: Readonly<Record<MusicVideoEffectFamily, string>> = {
  lyric: styles.lyric ?? '',
  title: styles.title ?? '',
  transition: styles.transition ?? '',
  shot: styles.shot ?? '',
};

/** Where the menu opens: above the button, its right edge on the right edge of the button. */
interface MenuPlace {
  bottom: number;
  right: number;
  maxHeight: number;
}

function placeOf(button: HTMLElement | null): MenuPlace {
  const rect = button?.getBoundingClientRect();
  if (rect === undefined) {
    return { bottom: 16, right: 16, maxHeight: 480 };
  }
  return {
    bottom: Math.max(8, window.innerHeight - rect.top + 6),
    right: Math.max(8, window.innerWidth - rect.right),
    maxHeight: Math.max(200, rect.top - 16),
  };
}

/**
 * "+ Add effect" in the timeline bar (design directed-video h3, spec 2026-10-17 §8): a menu of
 * every kind of the catalogue by family, with a search. A pick puts the effect at the play head
 * with the default params of its kind and selects it. The list comes from `MUSIC_VIDEO_EFFECTS`,
 * so a new kind shows with no UI code.
 */
const AddEffectMenuComponent: React.FC = () => {
  const intl = useIntl();
  const menuId = useId();
  const { doc, change, select, transport, readOnly, songMap } = useMusicVideoEditor();
  const [place, setPlace] = useState<MenuPlace | null>(null);
  const [query, setQuery] = useState('');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const isFull = doc.effects.length >= MUSIC_VIDEO_MAX_EFFECTS;
  const isOpen = place !== null && !readOnly && !isFull;

  const groups = useMemo(() => {
    const words = query.trim().toLowerCase();
    return FAMILY_ORDER.map((family) => ({
      family,
      kinds: MUSIC_VIDEO_EFFECT_KINDS.filter(
        (kind) =>
          MUSIC_VIDEO_EFFECTS[kind].family === family &&
          (words === '' || intl.formatMessage(effectKindMessages[kind]).toLowerCase().includes(words))
      ),
    })).filter((group) => group.kinds.length > 0);
  }, [intl, query]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Escape') {
          setPlace(null);
        }
        return;
      }
      const target = event.target as Node;
      if (menuRef.current?.contains(target) !== true && buttonRef.current?.contains(target) !== true) {
        setPlace(null);
      }
    };
    window.addEventListener('mousedown', close);
    window.addEventListener('keydown', close);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('keydown', close);
    };
  }, [isOpen]);

  const add = (kind: MusicVideoEffectKind) => {
    const id = newDocId('e');
    change((current) => addEffect(current, { id, kind, atSec: transport.livePositionSec() }));
    select({ kind: 'effect', id });
    setPlace(null);
    setQuery('');
  };

  const time = formatTime(transport.positionSec, true);
  const section = songMap.sections.find(
    (item) => transport.positionSec >= item.startSec && transport.positionSec < item.endSec
  );
  const sectionName =
    section === undefined ? null : `${section.label.charAt(0).toUpperCase()}${section.label.slice(1)}`;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={styles.button}
        disabled={readOnly || isFull}
        title={isFull ? intl.formatMessage(effectMessages.addFull) : undefined}
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        onClick={() => setPlace((current) => (current === null ? placeOf(buttonRef.current) : null))}
      >
        {intl.formatMessage(effectMessages.addEffect)}
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            className={styles.menu}
            role="dialog"
            aria-label={intl.formatMessage(effectMessages.addTitle)}
            style={{ bottom: place.bottom, right: place.right, maxHeight: place.maxHeight }}
          >
            <input
              className={styles.search}
              value={query}
              placeholder={intl.formatMessage(effectMessages.addSearch)}
              aria-label={intl.formatMessage(effectMessages.addSearch)}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- the menu opens for a search or a pick
              autoFocus
              onChange={(event) => setQuery(event.target.value)}
            />
            <p className={styles.hint}>
              {sectionName === null
                ? intl.formatMessage(effectMessages.addAt, { time })
                : intl.formatMessage(effectMessages.addAtSection, { time, section: sectionName })}
            </p>
            {groups.length === 0 && <p className={styles.hint}>{intl.formatMessage(effectMessages.addNone)}</p>}
            {groups.map((group) => (
              <div key={group.family} role="group" aria-label={intl.formatMessage(effectFamilyMessages[group.family])}>
                <div className={`${styles.family} ${FAMILY_CLASS[group.family]}`}>
                  <i aria-hidden />
                  {intl.formatMessage(effectFamilyMessages[group.family])}
                </div>
                {group.kinds.map((kind) => (
                  <button key={kind} type="button" className={styles.item} onClick={() => add(kind)}>
                    {intl.formatMessage(effectKindMessages[kind])}
                  </button>
                ))}
              </div>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export const AddEffectMenu = TypedMemo(AddEffectMenuComponent);
